import { useCallback, useState, useSyncExternalStore, type ReactNode } from 'react';
import type { BoardDoc } from '../doc/BoardDoc';
import {
  IconDots,
  IconDownload,
  IconFit,
  IconHome,
  IconHand,
  IconKanban,
  IconLayers,
  IconTable,
  IconTodo,
  IconPaperclip,
  IconMoon,
  IconNote,
  IconPen,
  IconShapes,
  IconFrame,
  IconText,
  IconRedo,
  IconSelect,
  IconSun,
  IconTitle,
  IconUndo,
  IconZoomIn,
  IconZoomOut,
  IconPlus,
} from '../ui/icons';
import { effectiveTheme, toggleTheme } from '../theme/theme';
import { exportBoardJson, selectAll, stressTest, zoomBy, zoomTo, zoomToFit, zoomToSelection } from './actions';
import { navigate, paths } from '../app/router';
import { useBoard, useBoardMeta, usePane, usePaneStore, useReadOnly } from './context';
import { importImageFiles, pickImageFiles } from './importImages';
import type { Tool } from './paneStore';
import { setToolSettings, useToolSettings } from './toolSettings';
import { usePanel } from './panels';
import { ToolOptions } from './ToolOptions';
import { PaneMenu } from '../ui/PaneMenu';
import { useIsMobile } from '../ui/useMediaQuery';
import { useUpdate } from '../app/version';

function useUndoState(board: BoardDoc): { canUndo: boolean; canRedo: boolean } {
  const bits = useSyncExternalStore(
    (cb) => {
      board.undo.on('stack-item-added', cb);
      board.undo.on('stack-item-popped', cb);
      board.undo.on('stack-cleared', cb);
      return () => {
        board.undo.off('stack-item-added', cb);
        board.undo.off('stack-item-popped', cb);
        board.undo.off('stack-cleared', cb);
      };
    },
    () => (board.undo.canUndo() ? 1 : 0) + (board.undo.canRedo() ? 2 : 0),
  );
  return { canUndo: (bits & 1) !== 0, canRedo: (bits & 2) !== 0 };
}

const CREATE_TOOLS: Tool[] = ['note', 'title', 'richtext', 'table', 'todo', 'kanban', 'shape', 'frame'];
const ADD_ITEMS: { tool: Tool; label: string; icon: () => React.JSX.Element }[] = [
  { tool: 'note', label: 'Sticky note', icon: IconNote },
  { tool: 'title', label: 'Title', icon: IconTitle },
  { tool: 'richtext', label: 'Text block', icon: IconText },
  { tool: 'table', label: 'Table', icon: IconTable },
  { tool: 'todo', label: 'To-do list', icon: IconTodo },
  { tool: 'kanban', label: 'Kanban', icon: IconKanban },
  { tool: 'shape', label: 'Shape (drag to size)', icon: IconShapes },
  { tool: 'frame', label: 'Frame (drag to size)', icon: IconFrame },
];

function ToolButton({
  tool,
  label,
  shortcut,
  children,
}: {
  tool: Tool;
  label: string;
  shortcut: string;
  children: ReactNode;
}) {
  const active = usePane((s) => s.tool === tool);
  const pane = usePaneStore();
  return (
    <button
      type="button"
      className={`tb-btn${active ? ' is-active' : ''}`}
      title={`${label} (${shortcut})`}
      aria-label={label}
      aria-pressed={active}
      onClick={() => pane.getState().setTool(tool)}
    >
      {children}
    </button>
  );
}

export function Toolbar() {
  const board = useBoard();
  const pane = usePaneStore();
  const ctx = { board, pane };
  const zoom = usePane((s) => s.camera.zoom);
  const tool = usePane((s) => s.tool);
  const { canUndo, canRedo } = useUndoState(board);
  const [theme, setTheme] = useState<'light' | 'dark'>(() => effectiveTheme());
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const menuOpen = menuAnchor !== null;
  const [addAnchor, setAddAnchor] = useState<HTMLElement | null>(null);
  const closeAdd = useCallback(() => setAddAnchor(null), []);
  const mobile = useIsMobile();
  const { panel, setPanel } = usePanel();
  const readOnly = useReadOnly();
  const isOwner = useBoardMeta().role === 'owner';
  const snap = useToolSettings((t) => t.snap);
  const snapGrid = useToolSettings((t) => t.snapGrid);
  const updateAvailable = useUpdate((s) => s.available !== null);

  const importImages = () => void pickImageFiles().then((files) => importImageFiles(ctx, files));

  const closeMenu = useCallback(() => setMenuAnchor(null), []);
  const menu = (action: () => void) => () => {
    setMenuAnchor(null);
    action();
  };

  return (
    <>
      <div className="toolbar" role="toolbar" aria-label="Tools">
      <a className="tb-btn" href={paths.dashboard} title="All boards" aria-label="All boards">
        <IconHome />
      </a>
      <div className="sep" />
      <ToolButton tool="select" label="Select" shortcut="V">
        <IconSelect />
      </ToolButton>
      <ToolButton tool="pan" label="Pan" shortcut="H or Space">
        <IconHand />
      </ToolButton>
      {!readOnly && (
      <>
      <div className="sep" />
      {mobile ? (
        <>
          <ToolButton tool="draw" label="Draw" shortcut="P pen, M marker, E eraser">
            <IconPen />
          </ToolButton>
          <button
            type="button"
            className={`tb-btn${addAnchor || CREATE_TOOLS.includes(tool) ? ' is-active' : ''}`}
            title="Add"
            aria-label="Add an item"
            aria-expanded={!!addAnchor}
            onClick={(e) => setAddAnchor(addAnchor ? null : e.currentTarget)}
          >
            <IconPlus />
          </button>
          {addAnchor && (
            <PaneMenu anchor={addAnchor} placement="right" onRequestClose={closeAdd} className="add-menu">
              {ADD_ITEMS.map(({ tool: t, label, icon: Icon }) => (
                <button
                  key={t}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setAddAnchor(null);
                    pane.getState().setTool(t);
                  }}
                >
                  <span>
                    <Icon /> {label}
                  </span>
                </button>
              ))}
              <div className="sep" />
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setAddAnchor(null);
                  importImages();
                }}
              >
                <span>
                  <IconPaperclip /> Import images / files
                </span>
              </button>
            </PaneMenu>
          )}
        </>
      ) : (
        <>
      <ToolButton tool="note" label="Sticky note" shortcut="N">
        <IconNote />
      </ToolButton>
      <ToolButton tool="title" label="Title" shortcut="T">
        <IconTitle />
      </ToolButton>
      <ToolButton tool="richtext" label="Text block" shortcut="X">
        <IconText />
      </ToolButton>
      <ToolButton tool="table" label="Table" shortcut="B">
        <IconTable />
      </ToolButton>
      <ToolButton tool="todo" label="To-do list" shortcut="D">
        <IconTodo />
      </ToolButton>
      <ToolButton tool="kanban" label="Kanban" shortcut="K">
        <IconKanban />
      </ToolButton>
      <ToolButton tool="shape" label="Shape" shortcut="S">
        <IconShapes />
      </ToolButton>
      <ToolButton tool="frame" label="Frame (container: moving it carries what is inside)" shortcut="F">
        <IconFrame />
      </ToolButton>
      <ToolButton tool="draw" label="Draw" shortcut="P pen, M marker, E eraser">
        <IconPen />
      </ToolButton>
      <button type="button" className="tb-btn" title="Import images or attach files (I)" aria-label="Import files" onClick={importImages}>
        <IconPaperclip />
      </button>
        </>
      )}
      <div className="sep" />
      <button
        type="button"
        className="tb-btn"
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
        disabled={!canUndo}
        onClick={() => board.undo.undo()}
      >
        <IconUndo />
      </button>
      <button
        type="button"
        className="tb-btn"
        title="Redo (Ctrl+Shift+Z)"
        aria-label="Redo"
        disabled={!canRedo}
        onClick={() => board.undo.redo()}
      >
        <IconRedo />
      </button>
      </>
      )}
      <div className="sep" />
      {!mobile && (
      <>
      <button type="button" className="tb-btn" title="Zoom in (+)" aria-label="Zoom in" onClick={() => zoomBy(pane, 1.25)}>
        <IconZoomIn />
      </button>
      <button
        type="button"
        className="tb-btn tb-zoom"
        title="Reset zoom (0)"
        aria-label="Reset zoom"
        onClick={() => zoomTo(pane, 1)}
      >
        {Math.round(zoom * 100)}%
      </button>
      <button type="button" className="tb-btn" title="Zoom out (-)" aria-label="Zoom out" onClick={() => zoomBy(pane, 0.8)}>
        <IconZoomOut />
      </button>
      </>
      )}
      <button type="button" className="tb-btn" title="Zoom to fit (1)" aria-label="Zoom to fit" onClick={() => zoomToFit(ctx)}>
        <IconFit />
      </button>
      <div className="sep" />
      {!readOnly && !mobile && (
      <button
        type="button"
        className={`tb-btn${panel === 'layers' ? ' is-active' : ''}`}
        title="Layers"
        aria-label="Layers"
        aria-pressed={panel === 'layers'}
        onClick={() => setPanel(panel === 'layers' ? null : 'layers')}
      >
        <IconLayers />
      </button>
      )}
      {!mobile && (
      <button
        type="button"
        className="tb-btn"
        title="Toggle theme"
        aria-label="Toggle theme"
        onClick={() => setTheme(toggleTheme())}
      >
        {theme === 'dark' ? <IconSun /> : <IconMoon />}
      </button>
      )}
      <div className="tb-menu-host">
        <button
          type="button"
          className={`tb-btn${menuOpen ? ' is-active' : ''}`}
          title="More"
          aria-label="More"
          aria-expanded={menuOpen}
          onClick={(e) => setMenuAnchor(menuOpen ? null : e.currentTarget)}
        >
          <IconDots />
        </button>
        {menuAnchor && (
          <PaneMenu anchor={menuAnchor} placement="right" onRequestClose={closeMenu}>
            <button type="button" role="menuitem" onClick={menu(() => zoomToFit(ctx))}>
              <span>Zoom to fit</span>
              <kbd>1</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => zoomToSelection(ctx))}>
              <span>Zoom to selection</span>
              <kbd>2</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => selectAll(ctx))}>
              <span>Select all</span>
              <kbd>Ctrl+A</kbd>
            </button>
            {mobile && (
              <>
                <div className="sep" />
                {!readOnly && (
                  <button type="button" role="menuitem" onClick={menu(() => setPanel(panel === 'layers' ? null : 'layers'))}>
                    <span>Layers…</span>
                  </button>
                )}
                <button type="button" role="menuitem" onClick={menu(() => setTheme(toggleTheme()))}>
                  <span>{theme === 'dark' ? 'Light theme' : 'Dark theme'}</span>
                </button>
              </>
            )}
            {!readOnly && (
            <>
            <div className="sep" />
            <button type="button" role="menuitemcheckbox" aria-checked={snap} onClick={() => setToolSettings({ snap: !snap })}>
              <span>{snap ? '☑' : '☐'} Snap to objects</span>
              <kbd>'</kbd>
            </button>
            <button type="button" role="menuitemcheckbox" aria-checked={snapGrid} onClick={() => setToolSettings({ snapGrid: !snapGrid })}>
              <span>{snapGrid ? '☑' : '☐'} Snap to grid</span>
            </button>
            <div className="sep" />
            <button type="button" role="menuitem" onClick={menu(() => setPanel('settings'))}>
              <span>Board settings…</span>
            </button>
            {isOwner && board.remote && (
              <button type="button" role="menuitem" onClick={menu(() => setPanel('share'))}>
                <span>Share…</span>
              </button>
            )}
            </>
            )}
            <button type="button" role="menuitem" onClick={menu(() => setPanel('export'))}>
              <span>Export PNG…</span>
              <IconDownload />
            </button>
            <button type="button" role="menuitem" onClick={menu(() => exportBoardJson(board))}>
              <span>Export board as JSON</span>
            </button>
            {board.remote && (
              <button type="button" role="menuitem" onClick={menu(() => navigate(paths.board(board.boardId, board.boardId)))}>
                <span>Split view</span>
              </button>
            )}
            <button type="button" role="menuitem" onClick={menu(() => setPanel('shortcuts'))}>
              <span>Keyboard shortcuts</span>
              <kbd>?</kbd>
            </button>
            <button type="button" role="menuitem" onClick={menu(() => setPanel('about'))}>
              <span>{updateAvailable ? 'About & updates… (new version)' : 'About & updates…'}</span>
            </button>
            {!readOnly && (
            <>
            <div className="sep" />
            <button type="button" role="menuitem" onClick={menu(() => stressTest(ctx, 1000))}>
              <span>Add 1000 test notes</span>
              <kbd>dev</kbd>
            </button>
            <button
              type="button"
              role="menuitem"
              className="danger"
              onClick={menu(() => {
                if (confirm('Delete every item on this board?')) {
                  board.clear();
                  pane.getState().clearSelection();
                }
              })}
            >
              <span>Clear board</span>
            </button>
            </>
            )}
          </PaneMenu>
        )}
      </div>
      </div>
      <ToolOptions />
    </>
  );
}
